/**
 * Layer Settings Persistence Hook
 *
 * Persists user's map layer toggle preferences to localStorage so they
 * don't have to reconfigure the canvas each time they visit.
 */

const STORAGE_KEY = 'firewatch-layer-settings';
// v2 (2026-05): introduced showPlannedBurns. On migration we force it ON
// for everyone so they can see planned-burn markers on the map by default;
// they can still toggle off afterwards.
const SETTINGS_VERSION = 2;

/**
 * Layer settings interface - all toggleable map layers.
 */
export interface LayerSettings {
  showWindLayer: boolean;
  showAircraftLayer: boolean;
  showFireBoundaries: boolean;
  showWarningAreas: boolean;
  showHotspots: boolean;
  showRoadClosures: boolean;
  showPowerOutages: boolean;
  showSmoke: boolean;
  showAirQuality: boolean;
  showCityTemperatures: boolean;
  showThunderstormWarnings: boolean;
  showLightningRisk: boolean;
  showPhotosLayer: boolean;
  // When false, planned-burn / hazard-reduction markers are hidden from
  // the map and lists. Default is ON — users typically want awareness of
  // any fire activity, including planned ones.
  showPlannedBurns: boolean;
  // Supporter-gated premium layers. Persisting the toggle is harmless for
  // non-supporters — Map.tsx re-checks the `highResLayers` / `satelliteImagery`
  // entitlement before actually adding either overlay, so a stale `true` here
  // never leaks a paid layer to a free user.
  showHighResImagery: boolean;
  showSatelliteImagery: boolean;
}

/**
 * Default layer settings - used when no saved settings exist.
 * These match the current defaults in App.tsx.
 */
export const DEFAULT_LAYER_SETTINGS: LayerSettings = {
  showWindLayer: false,
  showAircraftLayer: false,
  showFireBoundaries: true,
  showWarningAreas: true,
  showHotspots: false,
  showRoadClosures: false,
  showPowerOutages: false,
  showSmoke: false,
  showAirQuality: false,
  showCityTemperatures: false,
  showThunderstormWarnings: false,
  showLightningRisk: false,
  showPhotosLayer: false,
  showPlannedBurns: true,
  // HD imagery defaults ON so qualifying supporters keep the prior automatic
  // NSW high-res behaviour; satellite true-colour is a deliberate view, OFF.
  showHighResImagery: true,
  showSatelliteImagery: false,
};

interface StoredSettings {
  version: number;
  settings: LayerSettings;
}

/**
 * Load layer settings from localStorage.
 * Returns default settings if nothing is saved or on error.
 */
export function loadLayerSettings(): LayerSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return DEFAULT_LAYER_SETTINGS;
    }

    const parsed: StoredSettings = JSON.parse(stored);

    // Version check - if version mismatch, merge with defaults and apply
    // any one-time migrations. Migrating from v1 → v2 forces the new
    // showPlannedBurns toggle to its default (ON), even if the user had
    // a stale `false` value in storage.
    if (parsed.version !== SETTINGS_VERSION) {
      return {
        ...DEFAULT_LAYER_SETTINGS,
        ...parsed.settings,
        showPlannedBurns: DEFAULT_LAYER_SETTINGS.showPlannedBurns,
      };
    }

    // Merge with defaults to handle any missing keys
    return { ...DEFAULT_LAYER_SETTINGS, ...parsed.settings };
  } catch (error) {
    console.warn('[LayerSettings] Failed to load settings:', error);
    return DEFAULT_LAYER_SETTINGS;
  }
}

/**
 * Save layer settings to localStorage.
 */
export function saveLayerSettings(settings: LayerSettings): void {
  try {
    const data: StoredSettings = {
      version: SETTINGS_VERSION,
      settings,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('[LayerSettings] Failed to save settings:', error);
  }
}

/**
 * Clear saved layer settings (reset to defaults).
 */
export function clearLayerSettings(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('[LayerSettings] Failed to clear settings:', error);
  }
}
